import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearOrganizerToken,
  organizerLogin,
  organizerMe,
  setOrganizerToken,
} from '../lib/organizerApi';

const OrganizerAuthContext = createContext(null);

export function OrganizerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    const out = await organizerMe();
    if (out.user) {
      setUser(out.user);
      return out.user;
    }
    setUser(null);
    return null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const login = useCallback(async (identifier, password) => {
    setError(null);
    const out = await organizerLogin(identifier, password);
    if (!out.success || !out.token) {
      setError(out.error || 'Login failed');
      return { success: false, error: out.error };
    }
    setOrganizerToken(out.token);
    const u = out.user || (await refresh());
    setUser(u);
    return { success: true, user: u };
  }, [refresh]);

  const logout = useCallback(() => {
    clearOrganizerToken();
    setUser(null);
    setError(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    logout,
    refresh,
  }), [user, loading, error, login, logout, refresh]);

  return <OrganizerAuthContext.Provider value={value}>{children}</OrganizerAuthContext.Provider>;
}

export function useOrganizerAuth() {
  const ctx = useContext(OrganizerAuthContext);
  if (!ctx) throw new Error('useOrganizerAuth must be used within OrganizerAuthProvider');
  return ctx;
}
